import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import {
  ShieldCheck, Smartphone, Activity, Monitor, Globe, Download,
  Loader2, AlertTriangle, Check, X, Trash2, KeyRound, QrCode,
  Copy, RefreshCw, LogOut as LogOutIcon, Lock,
} from "lucide-react";
import { toast } from "sonner";

const SEV = { high: "#EF4444", medium: "#F59E0B", low: "#94A3B8" };
const GRADE_COLOR = { A: "#22C55E", B: "#84CC16", C: "#F59E0B", D: "#EF4444" };

const TABS = [
  { id: "posture", label: "Posture", icon: ShieldCheck },
  { id: "mfa", label: "MFA", icon: Smartphone },
  { id: "sessions", label: "Sessions", icon: Monitor },
  { id: "activity", label: "Login Activity", icon: Activity },
  { id: "allowlist", label: "IP Allowlist", icon: Globe },
  { id: "exports", label: "Data Exports", icon: Download },
];

export default function Security() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "posture";
  const setTab = (id) => setSearchParams({ tab: id });

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="security-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
          <span className="label-eyebrow">// Security & Access Management</span>
        </div>
        <h1 className="font-display text-3xl text-white tracking-tight">Security Center</h1>
        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
          Posture score, multi-factor authentication, active sessions, login activity with anomaly detection,
          organisation-level IP allowlist, and a complete audit trail of every data export.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-white/5" data-testid="security-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`security-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 flex items-center gap-2 font-mono text-[11px] tracking-widest transition-all border-b-2 -mb-px ${
              tab === t.id
                ? "border-[#D4AF37] text-white"
                : "border-transparent text-slate-400 hover:text-white"
            }`}
          >
            <t.icon className="w-3 h-3" strokeWidth={1.6} />
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      <div>
        {tab === "posture" && <Posture />}
        {tab === "mfa" && <Mfa />}
        {tab === "sessions" && <Sessions />}
        {tab === "activity" && <LoginActivity />}
        {tab === "allowlist" && <IpAllowlist />}
        {tab === "exports" && <Exports />}
      </div>
    </div>
  );
}

/* ─────────────────────────── Posture ─────────────────────────── */
function Posture() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/security/posture").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);
  if (loading) return <Loading />;
  if (!data) return null;
  const grade = data.grade;
  return (
    <div className="space-y-5" data-testid="posture-pane">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Score */}
        <div className="nexus-glass-panel p-6 lg:col-span-1 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-[80px] opacity-30" style={{ background: GRADE_COLOR[grade] }} />
          <div className="label-eyebrow mb-3">// Org Security Score</div>
          <div className="font-display text-7xl" style={{ color: GRADE_COLOR[grade] }} data-testid="posture-score">
            {data.score}
          </div>
          <div className="font-mono text-xs tracking-widest text-slate-400 mt-2">
            GRADE · <span style={{ color: GRADE_COLOR[grade] }}>{grade}</span>
          </div>
        </div>
        {/* Metrics */}
        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3">
          <Metric label="MFA coverage" value={`${data.metrics.mfa_percent}%`} sub={`${data.metrics.users_with_mfa} / ${data.metrics.users_total}`} />
          <Metric label="Active sessions" value={data.metrics.active_sessions} />
          <Metric label="Admin accounts" value={data.metrics.admin_accounts} />
          <Metric label="Allowlist entries" value={data.metrics.ip_allowlist_entries} />
          <Metric label="Failed logins 24h" value={data.metrics.failed_logins_24h} alert={data.metrics.failed_logins_24h >= 5} />
          <Metric label="Total users" value={data.metrics.users_total} />
        </div>
      </div>

      {/* Findings */}
      {data.findings.length === 0 ? (
        <div className="nexus-glass-panel p-6 text-center" data-testid="posture-clean">
          <Check className="w-7 h-7 mx-auto text-emerald-400 mb-2" />
          <h3 className="font-display text-lg text-white">No security findings</h3>
          <p className="text-sm text-slate-400 mt-1">Your institutional security posture is clean.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="posture-findings">
          <div className="label-eyebrow">// Findings · {data.findings.length}</div>
          {data.findings.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.04 }}
              className="nexus-card p-4 flex items-start gap-4"
              data-testid={`finding-${i}`}
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: SEV[f.severity] }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-display text-sm text-white">{f.title}</h4>
                  <span className="font-mono text-[9px] tracking-widest" style={{ color: SEV[f.severity] }}>
                    · {f.severity.toUpperCase()}
                  </span>
                </div>
                <p className="text-[12px] text-slate-400 leading-snug">{f.detail}</p>
              </div>
              {f.fix_route && (
                <a
                  href={f.fix_route}
                  className="shrink-0 text-[10px] font-mono tracking-widest px-3 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/8"
                >
                  {f.action.toUpperCase()} →
                </a>
              )}
            </motion.div>
          ))}
        </div>
      )}

      <EmailDeliveryCard />
    </div>
  );
}

/* ─────────────────────────── Email Delivery Diagnostics ─────────────────────────── */
function EmailDeliveryCard() {
  const { user } = useAuth();
  const [to, setTo] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (user?.email && !to) setTo(user.email); }, [user, to]);

  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/security/test-email", { to });
      setResult(r.data);
      if (r.data.ok) toast.success(`Test email sent to ${to} · id ${(r.data.id || "").slice(0, 12)}…`);
      else toast.error(r.data.reason || "Send failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="nexus-glass-panel p-5" data-testid="email-delivery-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow">// Email Delivery Test</div>
          <h3 className="font-display text-lg text-white mt-1">Verify outbound email reaches an inbox</h3>
          <p className="text-xs text-slate-400 mt-1 max-w-lg leading-relaxed">
            Sends a diagnostic email through the same pipeline used for invitations, password resets and admin notifications. Use this after DNS changes or when a user says they didn't get an invite.
          </p>
        </div>
      </div>
      <div className="mt-4 flex gap-2 items-center flex-wrap">
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          data-testid="test-email-to"
          placeholder="recipient@example.com"
          className="flex-1 min-w-[240px] bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 font-mono focus:border-[#D4AF37]/60 focus:outline-none"
        />
        <button
          onClick={send}
          disabled={busy || !to.includes("@")}
          data-testid="test-email-send"
          className="px-5 py-2 flex items-center gap-2 text-sm font-medium disabled:opacity-40"
          style={{ background: "var(--gold-500)", color: "#050B14" }}
        >
          {busy ? "Sending…" : "Send test email"}
        </button>
      </div>
      {result && (
        <div className="mt-3 p-3 bg-[#0A111F]/60 border border-white/5 text-xs font-mono" data-testid="test-email-result">
          <div className={result.ok ? "text-emerald-400" : "text-red-400"}>
            {result.ok ? "✓ DELIVERED" : "✗ FAILED"}
          </div>
          {result.id && <div className="text-slate-400 mt-1">Resend id · <span className="text-slate-200">{result.id}</span></div>}
          {result.reason && <div className="text-slate-400 mt-1">Reason · <span className="text-red-400">{result.reason}</span></div>}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── MFA ─────────────────────────── */
function Mfa() {
  const { user, refreshUser } = useAuth();
  const [enrolling, setEnrolling] = useState(null); // { secret, uri, qr_data_url }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const mfaEnabled = user?.mfa_enabled;

  const setup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/security/mfa/setup");
      setEnrolling(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start MFA enrollment");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post("/security/mfa/verify", { code: code.trim() });
      toast.success("Multi-factor authentication enabled");
      setEnrolling(null);
      setCode("");
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!code.trim()) return toast.error("Enter your current 6-digit code to disable");
    setBusy(true);
    try {
      await api.post("/security/mfa/disable", { code: code.trim() });
      toast.success("MFA disabled");
      setCode("");
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const copy = (s) => { navigator.clipboard.writeText(s); toast.success("Copied"); };

  return (
    <div className="space-y-4" data-testid="mfa-pane">
      <div className="nexus-glass-panel p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 flex items-center justify-center" style={{ background: mfaEnabled ? "rgba(34,197,94,0.12)" : "rgba(212,175,55,0.12)", border: `1px solid ${mfaEnabled ? "rgba(34,197,94,0.3)" : "rgba(212,175,55,0.3)"}` }}>
            <Smartphone className="w-4 h-4" style={{ color: mfaEnabled ? "#22C55E" : "var(--gold-400)" }} strokeWidth={1.6} />
          </div>
          <div>
            <h3 className="font-display text-lg text-white">Multi-Factor Authentication (TOTP)</h3>
            <div className="font-mono text-[10px] tracking-widest mt-0.5" style={{ color: mfaEnabled ? "#22C55E" : "#F59E0B" }}>
              {mfaEnabled ? "· ENABLED" : "· NOT ENABLED"}
            </div>
          </div>
        </div>
        <p className="text-sm text-slate-400 mt-3 max-w-2xl leading-relaxed">
          Use any authenticator app (Google Authenticator, 1Password, Authy, Microsoft Authenticator) to generate
          a 6-digit code each time you sign in. Removes the single-point-of-failure of a password alone.
        </p>

        {!mfaEnabled && !enrolling && (
          <button
            onClick={setup}
            disabled={busy}
            data-testid="mfa-start"
            className="mt-5 text-[11px] font-mono tracking-widest px-4 py-2.5 flex items-center gap-2 disabled:opacity-60"
            style={{ background: "var(--gold-500)", color: "#050B14" }}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />}
            ENROL DEVICE
          </button>
        )}

        {enrolling && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="mfa-enroll">
            <div>
              <div className="label-eyebrow mb-2">// Step 1 · Scan</div>
              <div className="inline-block p-3 bg-white">
                <img src={enrolling.qr_data_url} alt="MFA QR" className="w-44 h-44" data-testid="mfa-qr" />
              </div>
              <div className="mt-3">
                <div className="label-eyebrow mb-1">Or enter this secret manually</div>
                <div className="flex items-center gap-2 p-2.5 bg-[#0A111F] border border-white/10">
                  <code className="font-mono text-[12px] text-[#D4AF37] flex-1 break-all">{enrolling.secret}</code>
                  <button onClick={() => copy(enrolling.secret)} className="p-1.5 text-slate-400 hover:text-[#D4AF37]" data-testid="mfa-copy-secret">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-2">// Step 2 · Verify</div>
              <p className="text-[12px] text-slate-400 mb-3">Enter the 6-digit code your authenticator app shows now.</p>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                data-testid="mfa-code-input"
                className="w-full bg-[#0A111F] border border-white/10 text-white text-2xl font-mono tracking-widest text-center px-3 py-3 focus:border-[#D4AF37]/60 focus:outline-none"
              />
              <button
                onClick={verify}
                disabled={busy || code.length !== 6}
                data-testid="mfa-verify"
                className="mt-3 w-full text-[11px] font-mono tracking-widest py-2.5 disabled:opacity-50"
                style={{ background: "var(--gold-500)", color: "#050B14" }}
              >
                {busy ? "VERIFYING…" : "VERIFY & ENABLE MFA"}
              </button>
              <button
                onClick={() => { setEnrolling(null); setCode(""); }}
                className="mt-2 w-full text-[11px] font-mono tracking-widest py-2 border border-white/10 text-slate-400 hover:border-white/30"
              >
                CANCEL
              </button>
            </div>
          </motion.div>
        )}

        {mfaEnabled && !enrolling && (
          <div className="mt-6 pt-5 border-t border-white/5">
            <div className="label-eyebrow mb-2 text-red-400">// Disable MFA</div>
            <p className="text-[12px] text-slate-400 mb-3 max-w-md">
              Enter a current code from your authenticator to confirm. This will weaken your account security.
            </p>
            <div className="flex flex-wrap gap-2 max-w-md">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                data-testid="mfa-disable-code"
                className="flex-1 bg-[#0A111F] border border-white/10 text-white text-sm font-mono tracking-widest px-3 py-2 focus:border-red-500/60 focus:outline-none"
              />
              <button
                onClick={disable}
                disabled={busy || code.length !== 6}
                data-testid="mfa-disable"
                className="text-[11px] font-mono tracking-widest px-3 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
              >
                DISABLE MFA
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── Sessions ─────────────────────────── */
function Sessions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/security/sessions").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const revoke = async (sid) => {
    setBusy(sid);
    try {
      await api.post(`/security/sessions/${sid}/revoke`);
      toast.success("Session revoked");
      load();
    } catch {
      toast.error("Could not revoke");
    } finally {
      setBusy(null);
    }
  };

  const revokeAll = async () => {
    if (!confirm("Revoke all other sessions? You will remain signed in here, every other device will be signed out.")) return;
    setBusy("all");
    try {
      const r = await api.post(`/security/sessions/revoke-all`);
      toast.success(`${r.data.revoked} sessions revoked`);
      load();
    } catch {
      toast.error("Could not revoke");
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <Loading />;

  return (
    <div data-testid="sessions-pane">
      <div className="flex items-center justify-between mb-3">
        <span className="label-eyebrow">// Active & past sessions · {rows.length}</span>
        <button
          onClick={revokeAll}
          disabled={busy === "all" || rows.filter((r) => !r.revoked).length <= 1}
          data-testid="sessions-revoke-all"
          className="text-[10px] font-mono tracking-widest px-3 py-1.5 border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50 flex items-center gap-1.5"
        >
          <LogOutIcon className="w-3 h-3" /> REVOKE ALL OTHERS
        </button>
      </div>
      <div className="space-y-2">
        {rows.map((s) => (
          <div key={s.id} className="nexus-card p-4 flex items-center gap-4" data-testid={`session-${s.id}`}>
            <div className="w-9 h-9 flex items-center justify-center shrink-0" style={{ background: s.revoked ? "rgba(148,163,184,0.08)" : "rgba(34,197,94,0.10)", border: `1px solid ${s.revoked ? "rgba(148,163,184,0.2)" : "rgba(34,197,94,0.3)"}` }}>
              <Monitor className="w-4 h-4" style={{ color: s.revoked ? "#94A3B8" : "#22C55E" }} strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{s.user_agent || "Unknown device"}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                IP {s.ip} · Last seen {new Date(s.last_seen_at).toLocaleString()}
              </div>
            </div>
            <span className={`font-mono text-[9px] tracking-widest shrink-0 ${s.revoked ? "text-slate-500" : "text-emerald-400"}`}>
              {s.revoked ? "REVOKED" : "ACTIVE"}
            </span>
            {!s.revoked && (
              <button
                onClick={() => revoke(s.id)}
                disabled={busy === s.id}
                data-testid={`revoke-${s.id}`}
                className="shrink-0 p-2 border border-white/10 text-slate-400 hover:border-red-500/40 hover:text-red-400 disabled:opacity-50"
                title="Revoke this session"
              >
                {busy === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── Login Activity ─────────────────────────── */
function LoginActivity() {
  const { user } = useAuth();
  const canOrgScope = ["admin", "executive"].includes(user?.role);
  const [scope, setScope] = useState("me");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/security/login-activity?scope=${scope}&limit=100`)
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, [scope]);

  const statusColor = (s) =>
    s === "success" ? "#22C55E" :
    s === "mfa_required" ? "#F59E0B" :
    s === "ip_blocked" ? "#EF4444" :
    s === "bad_password" ? "#EF4444" : "#94A3B8";

  if (loading) return <Loading />;

  return (
    <div data-testid="activity-pane">
      {canOrgScope && (
        <div className="flex gap-1 mb-3">
          {[{ id: "me", label: "Me" }, { id: "org", label: "Whole org" }].map((s) => (
            <button
              key={s.id}
              onClick={() => setScope(s.id)}
              data-testid={`activity-scope-${s.id}`}
              className={`px-3 py-1.5 font-mono text-[10px] tracking-widest border ${scope === s.id ? "border-[#D4AF37]/60 bg-[#D4AF37]/8 text-white" : "border-white/10 text-slate-400 hover:border-white/25"}`}
            >
              {s.label.toUpperCase()}
            </button>
          ))}
        </div>
      )}
      <div className="space-y-1.5">
        {rows.length === 0 && (
          <div className="nexus-card p-6 text-center text-sm text-slate-400">No login activity yet.</div>
        )}
        {rows.map((r) => (
          <div key={r.id} className="nexus-card p-3 flex items-center gap-3" data-testid={`activity-${r.id}`}>
            <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: statusColor(r.status) }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">
                <span style={{ color: statusColor(r.status) }} className="font-mono text-[10px] tracking-widest mr-2">
                  {r.status.toUpperCase()}
                </span>
                IP {r.ip}
                {r.anomalies?.length > 0 && (
                  <span className="ml-2 font-mono text-[10px] text-amber-400">⚠ {r.anomalies.join(", ")}</span>
                )}
              </div>
              <div className="text-[11px] text-slate-500 truncate mt-0.5">{r.user_agent || "—"}</div>
            </div>
            <span className="font-mono text-[10px] text-slate-500 shrink-0">{new Date(r.timestamp).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── IP Allowlist ─────────────────────────── */
function IpAllowlist() {
  const { user } = useAuth();
  const canEdit = ["admin", "executive"].includes(user?.role);
  const [entries, setEntries] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/security/ip-allowlist").then((r) => setEntries(r.data.entries)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (entries.includes(v)) return toast.error("Already in list");
    setEntries([...entries, v]);
    setDraft("");
  };
  const remove = (e) => setEntries(entries.filter((x) => x !== e));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/security/ip-allowlist", { entries });
      setEntries(r.data.entries);
      toast.success(`Saved · ${r.data.entries.length} ${r.data.entries.length === 1 ? "entry" : "entries"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="nexus-glass-panel p-6" data-testid="allowlist-pane">
      <h3 className="font-display text-lg text-white">IP Allowlist</h3>
      <p className="text-sm text-slate-400 mt-1 max-w-2xl leading-relaxed">
        When set, only logins originating from these IPs or CIDR ranges are accepted for your organisation.
        Leave empty to allow logins from any IP. Supports both individual addresses (<code className="text-[#D4AF37]">203.0.113.42</code>) and CIDR ranges (<code className="text-[#D4AF37]">10.0.0.0/8</code>).
      </p>
      {canEdit && (
        <div className="mt-5 flex gap-2 max-w-md">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="e.g. 203.0.113.42 or 10.0.0.0/8"
            data-testid="allowlist-input"
            className="flex-1 bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none"
          />
          <button
            onClick={add}
            data-testid="allowlist-add"
            className="text-[11px] font-mono tracking-widest px-3 py-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/8"
          >
            ADD
          </button>
        </div>
      )}
      <div className="mt-5 space-y-2">
        {entries.length === 0 ? (
          <div className="text-[12px] text-slate-500">No entries — login allowed from any IP.</div>
        ) : (
          entries.map((e) => (
            <div key={e} className="flex items-center gap-3 p-2.5 border border-white/10 bg-[#0A111F]/60" data-testid={`allowlist-${e}`}>
              <Lock className="w-3 h-3 text-[#D4AF37] shrink-0" />
              <code className="font-mono text-[12px] text-white flex-1">{e}</code>
              {canEdit && (
                <button onClick={() => remove(e)} className="p-1 text-slate-500 hover:text-red-400" data-testid={`allowlist-rm-${e}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))
        )}
      </div>
      {canEdit && (
        <button
          onClick={save}
          disabled={busy}
          data-testid="allowlist-save"
          className="mt-5 text-[11px] font-mono tracking-widest px-4 py-2.5 disabled:opacity-50 flex items-center gap-2"
          style={{ background: "var(--gold-500)", color: "#050B14" }}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          SAVE ALLOWLIST
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────── Exports ─────────────────────────── */
function Exports() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/security/exports").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loading />;
  return (
    <div data-testid="exports-pane">
      <div className="label-eyebrow mb-3">// Data exports · {rows.length}</div>
      {rows.length === 0 ? (
        <div className="nexus-card p-6 text-center text-sm text-slate-400">
          No data exports recorded yet. Every PDF, PPTX, or bulk export action will appear here.
        </div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} className="nexus-card p-3 flex items-center gap-3" data-testid={`export-${r.id}`}>
              <Download className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">
                  <span className="font-mono text-[10px] tracking-widest mr-2 text-[#D4AF37]">{r.action.toUpperCase()}</span>
                  {r.entity}{r.entity_id ? ` · ${r.entity_id.slice(0, 8)}` : ""}
                </div>
                <div className="text-[11px] text-slate-500 truncate mt-0.5">
                  {r.actor_name || r.actor_id} {r.actor_email ? `· ${r.actor_email}` : ""}
                </div>
              </div>
              <span className="font-mono text-[10px] text-slate-500 shrink-0">
                {new Date(r.timestamp).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Shared ─────────────────────────── */
const Metric = ({ label, value, sub, alert }) => (
  <div className="nexus-card p-4">
    <div className="label-eyebrow">{label}</div>
    <div className="font-display text-2xl mt-1" style={{ color: alert ? "#EF4444" : "#F8FAFC" }}>{value}</div>
    {sub && <div className="text-[10px] font-mono tracking-widest text-slate-500 mt-1">{sub}</div>}
  </div>
);

const Loading = () => (
  <div className="p-10 text-slate-400 font-mono text-xs flex items-center gap-2">
    <Loader2 className="w-3 h-3 animate-spin" /> LOADING…
  </div>
);
